/**
 * Upsell del carrito: "COMPLETA TU LOOK" (Recommendations API) y
 * "PRODUCT CARE" (renderizado en servidor cuando hay calzado en el carrito;
 * ver snippets/timberland-cart-upsell-slot.liquid). Sin dependencias externas.
 * Carga diferida (script defer).
 *
 * El slot `[data-cart-upsell]` puede existir DOS VECES a la vez en el mismo
 * documento: en el cart-drawer (snippets/cart-drawer.liquid, renderizado en
 * todo el sitio vía layout/theme.liquid — su <script> es INCONDICIONAL, ver
 * comentario ahí) y en /cart (sections/main-cart-items.liquid, su <script>
 * solo se emite si cart != empty). Por eso:
 *   1) se evita ejecutar la IIFE dos veces (window.__tblCartUpsellInit).
 *   2) se itera sobre TODOS los slots presentes, no solo el primero.
 *
 * Dos tipos de slot:
 *   - data-cart-upsell-loaded="true" ya trae las tarjetas (Product Care):
 *     aquí solo se enlazan AGREGAR y las flechas (una vez por nodo).
 *   - slot vacío con data-product-id: se pide la sección
 *     timberland-cart-upsell a la Recommendations API y se inyecta.
 *
 * Rehidratación tras un re-render: assets/cart-drawer.js
 * (CartDrawer.renderContents) y assets/cart.js (CartItems.getSectionsToRender)
 * reescriben el HTML del carrito con `elemento.innerHTML = ...`. Un <script
 * src> insertado así NUNCA se re-ejecuta y Dawn no dispara ningún evento
 * propio al terminar, así que se observa con MutationObserver los dos
 * contenedores que SÍ persisten entre renders (#CartDrawer y
 * #main-cart-items) y se vuelve a procesar cualquier slot que reaparezca.
 */
(() => {
  if (window.__tblCartUpsellInit) return;
  window.__tblCartUpsellInit = true;

  // Misma técnica que assets/cart.js (getSectionInnerHTML): la respuesta de
  // /cart/add.js trae el HTML completo de cada sección pedida; se extrae el
  // fragmento del selector indicado y se inyecta en el contenedor real del
  // documento vivo (nunca se reemplaza el contenedor en sí, solo su
  // contenido — así el MutationObserver sigue observándolo).
  const reemplazarSeccion = (domId, selector, html) => {
    const contenedor = document.getElementById(domId);
    if (!contenedor || !html) return;
    const fragmento = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    if (!fragmento) return;
    const objetivo = contenedor.querySelector(selector) || contenedor;
    objetivo.innerHTML = fragmento.innerHTML;
  };

  // Contexto drawer: reemplaza #CartDrawer entero (renderContents) y lo abre.
  const agregarEnDrawer = async (btn) => {
    const r = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [{ id: Number(btn.dataset.variantId), quantity: 1 }],
        sections: 'cart-drawer,cart-icon-bubble',
      }),
    });
    const data = await r.json();
    if (!r.ok) throw new Error((data && data.description) || 'add failed');
    const drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.renderContents === 'function' && data.sections) {
      // renderContents() espera la respuesta CRUDA de /cart/add.js (misma
      // firma que assets/product-form.js). Si es el primer add de la sesión
      // el drawer aún tiene la clase is-empty y renderContents() no la quita
      // (mismo patrón que product-form.js:115): se quita ANTES.
      if (drawer.classList.contains('is-empty')) drawer.classList.remove('is-empty');
      drawer.renderContents(data);
    } else {
      window.location.reload();
    }
  };

  // Contexto página /cart: replica assets/cart.js CartItems.getSectionsToRender
  // para actualizar SOLO #main-cart-items (.js-contents), #main-cart-footer
  // (.js-contents) y #cart-icon-bubble, sin abrir el drawer ni navegar. Los
  // ids de sección reales se leen de cada data-id.
  const agregarEnPagina = async (btn) => {
    const idMainItems = document.getElementById('main-cart-items')?.dataset.id;
    const idMainFooter = document.getElementById('main-cart-footer')?.dataset.id;
    if (!idMainItems || !idMainFooter) {
      window.location.reload();
      return;
    }
    const r = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [{ id: Number(btn.dataset.variantId), quantity: 1 }],
        sections: [idMainItems, idMainFooter, 'cart-icon-bubble'].join(','),
      }),
    });
    const data = await r.json();
    if (!r.ok) throw new Error((data && data.description) || 'add failed');
    if (!data.sections) {
      window.location.reload();
      return;
    }
    reemplazarSeccion('main-cart-items', '.js-contents', data.sections[idMainItems]);
    reemplazarSeccion('main-cart-footer', '.js-contents', data.sections[idMainFooter]);
    reemplazarSeccion('cart-icon-bubble', '.shopify-section', data.sections['cart-icon-bubble']);
    // El slot de /cart vive dentro de #main-cart-items > .js-contents, así
    // que ya quedó regenerado por la línea de arriba; el MutationObserver lo
    // procesa solo.

    // Avisar al cart-drawer para que refresque líneas/subtotal/contador
    // (cart-drawer-items se suscribe a PUB_SUB_EVENTS.cartUpdate y, si el
    // source no es 'cart-items', dispara su propio onCartUpdate()).
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'tbl-cart-upsell', cartData: data });
    }
  };

  // Live region: primero el nodo estable de /cart (#cart-live-region-text),
  // que el flujo agregarEnPagina no reemplaza, y si no existe el del drawer
  // (mismo orden que assets/cart.js:311-312).
  const anunciarAgregado = (btn) => {
    const liveRegion =
      document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    if (liveRegion) liveRegion.textContent = btn.dataset.textoAgregado || 'Producto agregado al carrito';
  };

  const agregar = async (btn) => {
    if (btn.disabled) return;
    btn.disabled = true;
    try {
      if (btn.closest('#CartDrawer')) {
        await agregarEnDrawer(btn);
      } else {
        await agregarEnPagina(btn);
      }
      anunciarAgregado(btn);
    } catch {
      btn.disabled = true;
      btn.textContent = btn.dataset.textoAgotado || 'AGOTADO';
    }
  };

  // ---- Carrusel: una tarjeta por vista en el drawer, tres en /cart ----
  // Las flechas desplazan la pista un "viewport" (clientWidth) con
  // scroll-snap; se muestran solo cuando hay más tarjetas de las que caben y
  // cada una se oculta en su extremo.
  const enlazarCarrusel = (contenido) => {
    const pista = contenido.querySelector('.tbl-cart-upsell__pista');
    const prev = contenido.querySelector('[data-upsell-prev]');
    const next = contenido.querySelector('[data-upsell-next]');
    if (!pista || !prev || !next) return;

    const actualizar = () => {
      const desborda = pista.scrollWidth - pista.clientWidth > 4;
      prev.hidden = !desborda || pista.scrollLeft <= 4;
      next.hidden = !desborda || pista.scrollLeft + pista.clientWidth >= pista.scrollWidth - 4;
    };
    const paso = () => {
      const tarjeta = pista.querySelector('.tbl-upsell-card');
      const gap = parseFloat(getComputedStyle(pista).columnGap || getComputedStyle(pista).gap) || 0;
      return tarjeta ? tarjeta.getBoundingClientRect().width + gap : pista.clientWidth;
    };
    // Sin behavior:'smooth': dentro del drawer Chrome cancela el scroll suave
    // (verificado el 2026-09-03: scrollBy suave se quedaba en 0, instantáneo
    // sí avanzaba); el scroll-snap deja la tarjeta alineada igualmente.
    prev.addEventListener('click', () => pista.scrollBy({ left: -paso() }));
    next.addEventListener('click', () => pista.scrollBy({ left: paso() }));
    pista.addEventListener('scroll', actualizar, { passive: true });
    // El estado inicial de las flechas depende de un layout que aún no está
    // asentado cuando Dawn inyecta el HTML (renderContents inserta el nodo con
    // el drawer todavía fuera de pantalla y las imágenes en diferido), así que
    // se re-evalúa en varios momentos: al frame siguiente, al cambiar el
    // tamaño de la pista o de sus tarjetas, al cargar cada imagen, al entrar
    // en pantalla (el drawer al abrirse) y al redimensionar la ventana.
    requestAnimationFrame(actualizar);
    setTimeout(actualizar, 400);
    if ('ResizeObserver' in window) {
      const ro = new ResizeObserver(actualizar);
      ro.observe(pista);
      pista.querySelectorAll('.tbl-upsell-card').forEach((tarjeta) => ro.observe(tarjeta));
    }
    pista.querySelectorAll('img').forEach((img) => {
      if (!img.complete) img.addEventListener('load', actualizar, { once: true });
    });
    if ('IntersectionObserver' in window) {
      new IntersectionObserver((entradas) => {
        if (entradas.some((e) => e.isIntersecting)) actualizar();
      }).observe(pista);
    }
    window.addEventListener('resize', actualizar, { passive: true });
  };

  // Idempotente por nodo: el MutationObserver puede volver a pasar por aquí.
  const enlazar = (contenido) => {
    if (!contenido || contenido.dataset.tblBound === 'true') return;
    contenido.dataset.tblBound = 'true';
    contenido.querySelectorAll('[data-upsell-add]').forEach((btn) => btn.addEventListener('click', () => agregar(btn)));
    enlazarCarrusel(contenido);
  };

  // Caché de recomendaciones por product_id: cada re-render de Dawn deja el
  // slot vacío otra vez; sin esto se repetiría el fetch y se perdería la
  // posición del carrusel aunque el artículo base no haya cambiado.
  // Map: product_id -> nodo `.tbl-cart-upsell` ya enlazado (o null si no
  // hay recomendaciones utilizables). Si el nodo cacheado sigue insertado en
  // OTRO slot vivo (drawer y /cart coexisten), se clona y se enlaza el clon.
  const cacheRecomendaciones = new Map();

  const cargarSlot = async (slot) => {
    if (!slot) return;
    if (slot.dataset.cartUpsellLoaded === 'true') {
      // Product Care renderizado en servidor (o slot ya procesado).
      enlazar(slot.querySelector('.tbl-cart-upsell'));
      return;
    }
    const productoId = slot.dataset.productId;
    if (!productoId) return;
    // Se marca ANTES del fetch/caché para no duplicar si el observer vuelve
    // a disparar mientras esto sigue en curso.
    slot.dataset.cartUpsellLoaded = 'true';

    if (cacheRecomendaciones.has(productoId)) {
      const cacheado = cacheRecomendaciones.get(productoId);
      if (cacheado === null) return;
      let contenido = cacheado;
      if (cacheado.isConnected) {
        contenido = cacheado.cloneNode(true);
        delete contenido.dataset.tblBound;
        enlazar(contenido);
      }
      slot.replaceChildren(contenido);
      return;
    }

    try {
      const base =
        slot.dataset.recommendationsUrl || window.routes?.product_recommendations_url || '/recommendations/products';
      const url = `${base}?section_id=timberland-cart-upsell&product_id=${productoId}&limit=8&intent=related`;
      const r = await fetch(url);
      if (!r.ok) return;
      const html = new DOMParser().parseFromString(await r.text(), 'text/html');
      const contenido = html.querySelector('.tbl-cart-upsell');
      if (!contenido || contenido.dataset.productCount === '0' || !contenido.querySelector('.tbl-upsell-card')) {
        cacheRecomendaciones.set(productoId, null);
        return; // El slot se queda vacío, sin hueco visible.
      }
      cacheRecomendaciones.set(productoId, contenido);
      slot.replaceChildren(contenido);
      enlazar(contenido);
    } catch {
      /* silencio: sin upsell no hay hueco visible */
    }
  };

  const cargarTodos = () => {
    document.querySelectorAll('[data-cart-upsell]').forEach(cargarSlot);
  };

  const observer = new MutationObserver(cargarTodos);
  ['CartDrawer', 'main-cart-items'].forEach((id) => {
    const contenedor = document.getElementById(id);
    if (contenedor) observer.observe(contenedor, { childList: true, subtree: true });
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', cargarTodos);
  else cargarTodos();
})();
